import React, { useCallback, useRef } from 'react';

import './ColorPicker.scss';

const PRESET_COLORS = [
	'#0066CC',
	'#0891B2',
	'#059669',
	'#16A34A',
	'#CA8A04',
	'#EA580C',
	'#DC2626',
	'#DB2777',
	'#9333EA',
	'#6D28D9',
	'#4F46E5',
	'#1E1E21',
];

/** Props for the ColorPicker component — controlled by Form.Item */
interface ColorPickerProps {
	/** Current hex color value */
	value?: string;
	/** Callback when the color changes */
	onChange?: (value: string) => void;
	/** Callback when the field loses focus */
	onBlur?: () => void;
}

/** Hex color picker with preset swatches and custom input */
export function ColorPicker({ value = '', onChange, onBlur }: ColorPickerProps) {
	const nativeInputRef = useRef<HTMLInputElement>(null);

	const handlePresetClick = useCallback(
		(color: string) => {
			onChange?.(color);
		},
		[onChange],
	);

	const handleHexInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let val = e.target.value;
			if (val && !val.startsWith('#')) {
				val = '#' + val;
			}
			onChange?.(val);
		},
		[onChange],
	);

	const handleNativeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.value.toUpperCase());
		},
		[onChange],
	);

	const openNativePicker = useCallback(() => {
		nativeInputRef.current?.click();
	}, []);

	const currentColor = value || '';
	const isValidColor = /^#[0-9A-Fa-f]{6}$/.test(currentColor);

	return (
		<div className='color-picker' data-component='color-picker'>
			<div className='color-picker__presets'>
				{PRESET_COLORS.map((color) => (
					<button
						key={color}
						type='button'
						className='color-picker__preset'
						data-selected={currentColor.toUpperCase() === color.toUpperCase()}
						style={{ backgroundColor: color }}
						onClick={() => handlePresetClick(color)}
						aria-label={`Select color ${color}`}
					/>
				))}
			</div>

			<div className='color-picker__custom'>
				<button
					type='button'
					className='color-picker__swatch'
					style={{
						backgroundColor: isValidColor ? currentColor : '#FFFFFF',
					}}
					onClick={openNativePicker}
					aria-label='Open color picker'>
					{!isValidColor && <span className='color-picker__swatch-empty' />}
					<input
						ref={nativeInputRef}
						type='color'
						className='color-picker__native'
						value={isValidColor ? currentColor : '#0066CC'}
						onChange={handleNativeChange}
						tabIndex={-1}
					/>
				</button>
				<div className='color-picker__hex-input-wrapper'>
					<span className='color-picker__hex-prefix'>#</span>
					<input
						type='text'
						className='color-picker__hex-input'
						value={currentColor.replace(/^#/, '')}
						onChange={handleHexInputChange}
						onBlur={onBlur}
						placeholder='0066CC'
						maxLength={6}
						spellCheck={false}
						autoComplete='off'
					/>
				</div>
			</div>
		</div>
	);
}
